import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Matter } from '@/lib/store';
import { canViewFinancials } from '@/lib/permissions';
import { useUIConfig } from '@/config/ui-config';
import { BillingRatesForm, BillingRateEntry } from './BillingRatesForm';
import { MatterPhasesForm, MatterPhaseEntry } from './MatterPhasesForm';
import { FeeSharingConfigsForm } from './FeeSharingConfigsForm';
import type { FeeSharingConfigPayload } from '@/types/fee-sharing';
import api from '@/lib/api';
import { ROUTES } from '@/constants/routes';

interface MatterFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  matter?: Matter | null;
  onSuccess: () => void;
  firmId?: string;
}

type FormData = Omit<Matter, 'id' | 'status' | 'billing_rates' | 'fee_sharing_configs' | 'phases'> & { 
  status?: 'active' | 'inactive';
  billing_rates: BillingRateEntry[];
  invoice_format?: string;
  fee_sharing_configs?: FeeSharingConfigPayload[];
  phases?: MatterPhaseEntry[];
};

export function MatterFormDialog({
  isOpen,
  onClose,
  matter,
  onSuccess,
  firmId
}: MatterFormDialogProps) {
  const { toast } = useToast();
  const { matter: matterConfig, form: formConfig } = useUIConfig(firmId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialFormData: FormData = {
    name: '',
    group_name: '',
    billing_rates: [],
    phases: [],
    budget: 0,
    billable: true,
    email_on_budget_hit: false,
    rounding: false,
    rounding_type: matterConfig.defaultRoundingType,
    rounding_amount: matterConfig.defaultRoundingAmount,
    status: 'active',
    invoice_format: 'standard',
  };

  const [formData, setFormData] = useState<FormData>(initialFormData);

  useEffect(() => {
    if (matter) {
      const { status, billing_rates = [], fee_sharing_configs, phases = [], ...rest } = matter;
      setFormData({
        ...rest,
        billing_rates,
        phases: phases.map((p, i) => ({
          id: p.id,
          name: p.name,
          description: p.description || '',
          budget: Number(p.budget) || 0,
          order: p.order ?? i,
          start_date: p.start_date || null,
          end_date: p.end_date || null,
        })),
        status: status === 'active' ? 'active' : 'inactive',
      });
    } else {
      setFormData(initialFormData);
    }
  }, [matter, matterConfig.defaultRoundingType, matterConfig.defaultRoundingAmount]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Matter name is required',
        variant: 'destructive',
      });
      return;
    }

    if (matterConfig.requireGroupName && !formData.group_name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Group name is required',
        variant: 'destructive',
      });
      return;
    }

    if (formData.budget && formData.budget > matterConfig.maxBudget) {
      toast({
        title: 'Validation Error',
        description: `Budget cannot exceed $${matterConfig.maxBudget.toLocaleString()}`,
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (matter) {
        await api.put(`${ROUTES.API.MATTERS}${matter.id}/`, formData);
        toast({
          title: 'Success',
          description: 'Matter updated successfully',
        });
      } else {
        await api.post(`${ROUTES.API.MATTERS}`, formData);
        toast({
          title: 'Success',
          description: 'Matter created successfully',
        });
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || (matter ? 'Failed to update matter' : 'Failed to create matter'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBillingRatesChange = (billingRates: BillingRateEntry[]) => {
    setFormData(prev => ({ ...prev, billing_rates: billingRates }));
  };

  const handleFeeSharingChange = (configs: FeeSharingConfigPayload[]) => {
    setFormData(prev => ({ ...prev, fee_sharing_configs: configs }));
  };

  const handlePhasesChange = (phases: MatterPhaseEntry[]) => {
    setFormData(prev => ({ ...prev, phases }));
  };

  // Check if user can see financial data
  const currentUser = useAuth((s) => s.user);
  const userGroups = currentUser?.groups ?? [];
  const canViewFinancialsFlag = canViewFinancials(userGroups);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${matter && canViewFinancialsFlag ? 'sm:max-w-[900px]' : 'sm:max-w-[800px]'} max-h-[90vh]`}>
        <DialogHeader>
          <DialogTitle>
            {matter ? 'Edit Matter' : 'Add New Matter'}
          </DialogTitle>
          <DialogDescription>
            {matter ? 'Update matter details below' : 'Enter matter details below'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="overflow-y-auto pr-6 -mr-6 pl-1 max-h-[calc(90vh-180px)]">
          <form onSubmit={handleSubmit} className={formConfig.fieldSpacing}>
            {/* Basic Information */}
            <div className={`grid grid-cols-${formConfig.defaultColumns} gap-4`}>
              <div className="grid gap-2">
                <Label htmlFor="name" className={formConfig.labelFontWeight}>
                  Matter Name {formConfig.requiredFieldMarker}
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="group_name" className={formConfig.labelFontWeight}>
                  Group Name {matterConfig.requireGroupName ? formConfig.requiredFieldMarker : ''}
                </Label>
                <Input
                  id="group_name"
                  value={formData.group_name}
                  onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                  required={matterConfig.requireGroupName}
                />
              </div>
            </div>

            {/* Fee Sharing — visible to privileged users */}
            {canViewFinancialsFlag && (
              <FeeSharingConfigsForm
                initialConfigs={matter?.fee_sharing_configs}
                onChange={handleFeeSharingChange}
                firmId={firmId}
              />
            )}

            {/* Billing Rates */}
            <BillingRatesForm
              rates={formData.billing_rates}
              onChange={handleBillingRatesChange}
              firmId={firmId}
            />

            {/* Phases */}
            <MatterPhasesForm
              phases={formData.phases || []}
              onChange={handlePhasesChange}
              firmId={firmId}
            />

            {/* Financial Settings */}
            <div className="space-y-4">
              <Label className={`text-base ${formConfig.labelFontWeight}`}>
                Financial Settings
              </Label>
              
              <div className={`grid grid-cols-${formConfig.defaultColumns} gap-4`}>
                <div className="grid gap-2">
                  <Label htmlFor="budget" className={formConfig.labelFontWeight}>
                    Budget (€)
                  </Label>
                  <Input
                    id="budget"
                    type="number"
                    min="0"
                    max={matterConfig.maxBudget}
                    value={formData.budget || ''}
                    onChange={(e) => setFormData({ ...formData, budget: Number(e.target.value) || 0 })}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="invoice_format" className={formConfig.labelFontWeight}>
                    Invoice Format
                  </Label>
                  <Select
                    value={formData.invoice_format || 'standard'}
                    onValueChange={(value) => setFormData({ ...formData, invoice_format: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="ledes_1998b">LEDES 1998B</SelectItem>
                      <SelectItem value="ledes_1998bi">LEDES 1998BI</SelectItem>
                      <SelectItem value="ledes_2000">LEDES 2000</SelectItem>
                      <SelectItem value="ledes_xml_2_1">LEDES XML 2.1</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <label
                  htmlFor="billable"
                  className="flex items-center justify-between gap-3 border rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                >
                  <span className={`text-sm ${formConfig.labelFontWeight}`}>Billable Matter</span>
                  <Switch
                    id="billable"
                    checked={formData.billable}
                    onCheckedChange={(checked) => setFormData({ ...formData, billable: checked })}
                  />
                </label>

                <label
                  htmlFor="email_on_budget_hit"
                  className="flex items-center justify-between gap-3 border rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                >
                  <span className={`text-sm ${formConfig.labelFontWeight}`}>Email on Budget Hit</span>
                  <Switch
                    id="email_on_budget_hit"
                    checked={formData.email_on_budget_hit}
                    onCheckedChange={(checked) => setFormData({ ...formData, email_on_budget_hit: checked })}
                  />
                </label>

                <label
                  htmlFor="rounding"
                  className="flex items-center justify-between gap-3 border rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                >
                  <span className={`text-sm ${formConfig.labelFontWeight}`}>Time Rounding</span>
                  <Switch
                    id="rounding"
                    checked={formData.rounding}
                    onCheckedChange={(checked) => setFormData({ ...formData, rounding: checked })}
                  />
                </label>
              </div>

              {formData.rounding && (
                <div className={`grid grid-cols-${formConfig.defaultColumns} gap-4`}>
                  <div className="grid gap-2">
                    <Label htmlFor="rounding_type" className={formConfig.labelFontWeight}>
                      Rounding Type
                    </Label>
                    <Select
                      value={formData.rounding_type}
                      onValueChange={(value: 'nearest' | 'up' | 'down') =>
                        setFormData({ ...formData, rounding_type: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nearest">Round to Nearest</SelectItem>
                        <SelectItem value="up">Round Up</SelectItem>
                        <SelectItem value="down">Round Down</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="rounding_amount" className={formConfig.labelFontWeight}>
                      Rounding Amount (minutes)
                    </Label>
                    <Select
                      value={String(formData.rounding_amount)}
                      onValueChange={(value) =>
                        setFormData({ ...formData, rounding_amount: Number(value) })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {matterConfig.roundingAmountOptions.map((option) => (
                          <SelectItem key={option} value={String(option)}>
                            {option === 0 ? 'No rounding' : `${option} minutes`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className={isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {isSubmitting && formConfig.showLoadingSpinner && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                )}
                {matter ? 'Update Matter' : 'Create Matter'}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
